/**
 * Codes d'erreur centralisés — source unique partagée front + back.
 * Format d'erreur unifié de l'API : `{ error: { code, message } }`.
 */
export const ErrorCode = {
    VALIDATION: 'VALIDATION',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    /**
     * Conflit d'unicité PRÉCIS sur la CRÉATION d'un compte : l'e-mail est déjà pris
     * par un compte ACTIF. Renvoyé en 409 par `POST /api/admin/users` — sous-cas
     * machine-lisible de `CONFLICT`, pour que l'UI affiche « Cet e-mail est déjà
     * utilisé » plutôt qu'un message générique e-mail/chemin.
     */
    EMAIL_TAKEN: 'EMAIL_TAKEN',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL: 'INTERNAL',
    /**
     * Écriture désactivée sur la bibliothèque : une mutation disque (déplacement de
     * fichiers) a été demandée alors que `LIBRARY_WRITABLE` est faux (bibliothèque
     * montée en lecture seule). Renvoyé en 409 par `POST /api/libraries/:id/move`.
     */
    WRITE_DISABLED: 'WRITE_DISABLED',
    /**
     * Auto-suppression interdite : un compte ne peut pas se supprimer lui-même.
     * Renvoyé en 409 par `DELETE /api/admin/users/:id` quand la cible est l'auteur
     * de la requête (conflit d'état, distinct du garde-fou « dernier superadmin »).
     */
    CANNOT_DELETE_SELF: 'CANNOT_DELETE_SELF',
    /**
     * Aucun fournisseur LLM par défaut activé pour la bibliothèque : le tagging IA
     * (`POST /api/libraries/:id/ai-tag`) a été demandé mais aucun fournisseur de
     * vision n'est configuré/activé. Renvoyé en 409 → l'UI renvoie l'utilisateur vers
     * « Configuration LLM » (Admin).
     */
    AI_PROVIDER_MISSING: 'AI_PROVIDER_MISSING',
    /**
     * Fonctions IA globalement DÉSACTIVÉES par le superadmin : le tagging IA
     * (`POST /api/libraries/:id/ai-tag`) a été demandé alors que le coupe-circuit
     * global `ai_enabled` est faux. Renvoyé en 409 AVANT toute création de job →
     * l'UI masque déjà l'entrée « Tagger avec l'IA », ce 409 est la défense en
     * profondeur côté serveur.
     */
    AI_DISABLED: 'AI_DISABLED',
    /**
     * Cible de lien d'accès invalide : `POST /api/session-links` a visé un compte
     * qui ne peut pas recevoir de lien d'accès — soit un compte `superadmin` (refus
     * de principe), soit un compte n'appartenant à AUCUNE bibliothèque (ni
     * propriétaire ni bénéficiaire). Renvoyé en 403 (distinct du 404 « compte
     * introuvable/inactif »).
     */
    SESSION_LINK_TARGET_INVALID: 'SESSION_LINK_TARGET_INVALID',
    /**
     * VERROUILLAGE OPTIMISTE — la ressource a été modifiée par quelqu'un d'autre depuis
     * la lecture du client : l'`expectedUpdatedAt` fourni ne correspond plus à
     * l'`updated_at` en base, la mise à jour n'a donc touché AUCUNE ligne.
     *
     * Renvoyé en 409 par `PATCH /api/app-settings` (réglages superadmin) et
     * `PATCH /api/libraries/:id/saved-filters/:filterId` (édition d'expression). Sous-cas
     * machine-lisible de `CONFLICT` : l'UI doit proposer de RECHARGER plutôt qu'afficher
     * une erreur générique (l'écrasement silencieux du travail de l'autre éditeur est
     * précisément ce que ce code évite).
     */
    STALE_UPDATE: 'STALE_UPDATE',
    /**
     * Un job du même TYPE est déjà `queued` ou `running` sur la bibliothèque : inutile
     * d'en empiler un second (le moteur est strictement sérialisé, le doublon ne ferait
     * que rejouer le même travail). Renvoyé en 409 par `POST /api/libraries/:id/scan`.
     *
     * ⚠ La garde est DÉLIBÉRÉMENT limitée au scan déclenché à la main : le téléversement
     * enfile lui aussi un `scan` (indexation des fichiers reçus) et doit TOUJOURS pouvoir
     * le faire, sous peine de laisser des médias non indexés.
     */
    JOB_ALREADY_RUNNING: 'JOB_ALREADY_RUNNING',
    /**
     * Ce TYPE de job n'est pas relançable (`POST /api/jobs/:id/retry`, audit B3).
     *
     * Seuls les jobs à la fois IDEMPOTENTS et non destructifs sur `/library` sont
     * ré-enfilables (`RETRYABLE_JOB_TYPES` dans `job.ts`, qui porte la justification
     * type par type). Rejouer une rotation la composerait ; rejouer une purge de
     * corbeille détruirait ce qui y a été mis depuis l'échec. Renvoyé en 409, avec un
     * message explicite plutôt qu'un bouton dangereux.
     */
    JOB_NOT_RETRYABLE: 'JOB_NOT_RETRYABLE',
    /**
     * Déplacement de DOSSIER entre VOLUMES (XPHOTO-32, lot 4) : `rename()` a répondu
     * `EXDEV`, la source et la destination ne sont pas sur le même système de fichiers
     * (montage exotique, jonction, point de montage imbriqué dans la bibliothèque).
     *
     * Renvoyé en 409, et DÉLIBÉRÉMENT distinct de `CONFLICT` : ce n'est pas « la place est
     * prise », c'est « cette opération n'est pas réalisable ici » — deux causes qui
     * appellent deux conduites différentes chez l'utilisateur (renommer autrement vs
     * renoncer). Aucun repli sur une copie récursive : copier puis effacer une
     * arborescence entière est une tout autre classe de risque.
     */
    CROSS_DEVICE: 'CROSS_DEVICE',
    /**
     * INCOHÉRENCE BASE ↔ DISQUE (XPHOTO-32, lot 4) : le répertoire a bien été renommé sur
     * le disque, la transaction de propagation a échoué, ET la compensation (remettre le
     * répertoire à son nom d'origine) a échoué elle aussi. La base décrit donc une
     * arborescence qui n'existe plus.
     *
     * Renvoyé en 500, avec une entrée `file_op_errors` (`DB_OUT_OF_SYNC`) qui nomme les
     * deux chemins. Un code DÉDIÉ parce que la conduite à tenir est précise et unique —
     * relancer une analyse de la bibliothèque — et qu'un « erreur interne » générique ne
     * la dirait pas.
     */
    DB_OUT_OF_SYNC: 'DB_OUT_OF_SYNC',
    /**
     * SUPPRESSION CIBLÉE REFUSÉE — le sous-arbre n'est pas vide (XPHOTO-32, lot 5) : il
     * contient un fichier hors liste blanche, un répertoire caché sanctuarisé
     * (`.stfolder`…) ou un répertoire illisible ; ou bien il ne contient que des fichiers
     * système et l'option « effacer aussi les fichiers système » n'a pas été cochée.
     *
     * Renvoyé en 409 par `POST /api/libraries/:id/folders/delete`. Code DÉDIÉ parce que la
     * conduite à tenir — sortir ou effacer ce qui reste dans le dossier — n'a rien à voir
     * avec celle du `CONFLICT` des trois autres écritures (« un dossier porte déjà ce
     * nom »), qui serait le message affiché sans lui.
     */
    FOLDER_NOT_EMPTY: 'FOLDER_NOT_EMPTY',
    /**
     * SUPPRESSION CIBLÉE REFUSÉE — des médias INDEXÉS référencent encore le sous-arbre
     * (XPHOTO-32, lot 5), y compris des lignes `is_missing = true` dont le fichier a déjà
     * disparu du disque.
     *
     * Renvoyé en 409 par `POST /api/libraries/:id/folders/delete`. Distinct de
     * `FOLDER_NOT_EMPTY` parce que la sortie est ailleurs : ce n'est pas le disque qu'il
     * faut vider mais l'INDEX, via les écrans « Poubelle » et « Fichiers absents » de la
     * console Admin. Sans ce refus, le répertoire partirait du disque et le dossier
     * continuerait d'apparaître dans l'onglet Dossiers (les facettes le dérivent
     * d'`assets.dir_path`).
     */
    FOLDER_HAS_ASSETS: 'FOLDER_HAS_ASSETS',
};
//# sourceMappingURL=errors.js.map